import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SurfReport from "@/components/SurfReport";
import TideChart from "@/components/TideChart";
import ShareButton from "@/components/ShareButton";
import BackToTopButton from "@/components/BackToTopButton";
import { useAlertCount } from "@/components/AlertsPanel";
import { useSEO } from "@/hooks/useSEO";
import { useJsonLd } from "@/hooks/useJsonLd";
import { useI18n } from "@/contexts/I18nContext";
import { scrollToPageTop } from "@/lib/scroll";
import { BEACH_GUIDES, localizeBeach } from "@/data/beachGuide";
import { ArrowLeft, MapPin, Waves, Wind, Compass, Sun, Umbrella, TriangleAlert, Sparkles, Car, ExternalLink, Loader2 } from "lucide-react";

/**
 * Dedicated per-beach detail page. Renders:
 * - Hero with beach name, tagline, difficulty
 * - Live conditions (surf / tide / weather) from /api/surf/{beach_id}
 * - Best-tide, best-wind, board recommendation
 * - Season quality bars
 * - Hazards + local tips lists
 * - "How to get there" card with Google Maps + driving directions links
 *
 * All localizable copy is fed through `localizeBeach(beach, lang)` so the
 * whole page renders in Spanish when the user has ES selected and gracefully
 * falls back to English otherwise.
 */
export default function BeachDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const alertCount = useAlertCount();
  const { lang } = useI18n();
  React.useEffect(() => { scrollToPageTop(); }, [slug]);

  // Match slug to the guide — support both "maderas" and "playa-maderas"
  const guide = React.useMemo(() => {
    const raw = Object.values(BEACH_GUIDES).find(
      (b) => b.slug === slug || b.id === slug || b.id === `playa-${slug}`
    );
    return raw ? localizeBeach(raw, lang) : null;
  }, [slug, lang]);

  useSEO({
    title: guide
      ? `${guide.name} · surf guide, conditions & tides — Popoyo.app`
      : "Beach — Popoyo.app",
    description: guide
      ? `${guide.tagline} Live conditions, best tide, seasons, directions & local tips for ${guide.name} near Popoyo, Nicaragua.`
      : "Popoyo beach guide.",
    // Dynamic 1200×630 OG card with today's wave height + wind for great link previews
    image: guide ? `https://popoyo.app/api/og/beach/${guide.slug || slug}.png` : undefined,
    path: `/surf/${slug}`,
  });

  // Per-beach structured data (BreadcrumbList + FAQPage + Beach) so each surf
  // guide can rank on its own with rich results in Google.
  const beachSlug = guide?.slug || slug;
  const beachUrl = `https://popoyo.app/surf/${beachSlug}`;
  const es = lang === "es";
  const faqs = React.useMemo(() => {
    if (!guide) return [];
    const seasons = (guide.bestSeasons || [])
      .map((s) => `${s.months} — ${s.label}`)
      .join("; ");
    const dir = guide.directions || {};
    const out = [
      {
        q: es ? `¿Qué tipo de ola es ${guide.name}?` : `What kind of wave is ${guide.name}?`,
        a: `${guide.waveStyle}. ${guide.tagline}`,
      },
      {
        q: es
          ? `¿Cuál es la mejor marea y viento para surfear ${guide.name}?`
          : `What are the best tide and wind for surfing ${guide.name}?`,
        a: es
          ? `Marea: ${guide.bestTide}. Viento: ${guide.bestWind}.`
          : `Tide: ${guide.bestTide}. Wind: ${guide.bestWind}.`,
      },
      {
        q: es ? `¿Para qué nivel de surfista es ${guide.name}?` : `What skill level is ${guide.name} for?`,
        a: es
          ? `${guide.difficulty}. Tabla recomendada: ${guide.boardRecommended}.`
          : `${guide.difficulty}. Recommended board: ${guide.boardRecommended}.`,
      },
    ];
    if (seasons) {
      out.push({
        q: es ? `¿Cuándo es la mejor época para surfear ${guide.name}?` : `When is the best time to surf ${guide.name}?`,
        a: seasons,
      });
    }
    if (dir.from) {
      out.push({
        q: es ? `¿Cómo llego a ${guide.name}?` : `How do I get to ${guide.name}?`,
        a: `${es ? "Desde" : "From"} ${dir.from} — ${dir.distance || ""}. ${dir.routeNote || ""}`.trim(),
      });
    }
    return out;
  }, [guide, es]);

  useJsonLd(
    guide
      ? [
          {
            id: "ld-beach-breadcrumb",
            data: {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Popoyo", item: "https://popoyo.app/" },
                { "@type": "ListItem", position: 2, name: es ? "Playas" : "Beaches", item: "https://popoyo.app/beaches" },
                { "@type": "ListItem", position: 3, name: guide.name, item: beachUrl },
              ],
            },
          },
          {
            id: "ld-beach-faq",
            data: {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          },
          {
            id: "ld-beach-place",
            data: {
              "@context": "https://schema.org",
              "@type": ["TouristAttraction", "Beach"],
              name: guide.name,
              description: guide.tagline,
              url: beachUrl,
              image: `https://popoyo.app/api/og/beach/${beachSlug}.png`,
              address: {
                "@type": "PostalAddress",
                addressLocality: "Popoyo, Tola",
                addressRegion: "Rivas",
                addressCountry: "NI",
              },
              containedInPlace: { "@type": "Place", name: "Popoyo, Tola, Rivas, Nicaragua" },
              isAccessibleForFree: true,
            },
          },
        ]
      : []
  );

  const surfQ = useQuery({
    queryKey: ["surf", guide?.id],
    queryFn: () => http.get(`/surf/${guide.id}?days=3`).then((r) => r.data),
    enabled: !!guide,
    staleTime: 15 * 60 * 1000,
  });

  if (!guide) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header alertCount={alertCount} />
        <main className="flex-1 max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Beach not found</h1>
          <p className="text-[color:var(--ink-2)] mb-6">We don&apos;t have a guide for &quot;{slug}&quot; yet.</p>
          <button onClick={() => navigate("/")} className="pill px-4 py-2 bg-[color:var(--coral)] text-white">
            <ArrowLeft className="w-4 h-4 inline mr-1.5" /> Back home
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const data = surfQ.data;

  return (
    <div className="min-h-screen flex flex-col" data-testid={`beach-detail-${guide.slug}`}>
      <Header alertCount={alertCount} />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        {/* Back + Share top bar */}
        <div className="flex items-center gap-2">
          <Link
            to="/"
            data-testid="beach-back-home"
            className="inline-flex items-center gap-1.5 text-sm text-[color:var(--ink-2)] hover:text-[color:var(--coral)]"
          >
            <ArrowLeft className="w-4 h-4" /> Popoyo.app
          </Link>
          <div className="ml-auto">
            <ShareButton
              path={`/surf/${guide.slug}`}
              title={`${guide.name} · surf guide — Popoyo.app`}
              text={guide.tagline}
              testId="share-beach-btn"
            />
          </div>
        </div>

        {/* Hero */}
        <section
          className="clay overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${guide.color} 0%, ${guide.color}CC 100%)` }}
        >
          {guide.photoUrl && (
            <>
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${guide.photoUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center 55%",
                }}
              />
              {/* Legibility overlay — keeps title contrast without hiding the photo */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)`,
                }}
              />
            </>
          )}
          <div className="relative px-6 sm:px-10 py-10 md:py-14 text-white">
            <p className="eyebrow" style={{ color: "rgba(255,255,255,0.9)" }}>Surf guide · Popoyo</p>
            <h1
              className="mt-1 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white"
              style={{ fontFamily: "Outfit", textShadow: "0 2px 18px rgba(0,0,0,0.55)" }}
              data-testid="beach-title"
            >
              {guide.name}
            </h1>
            <p className="mt-3 text-base sm:text-lg max-w-2xl text-white" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.55)" }}>
              {guide.tagline}
            </p>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <DifficultyPill level={guide.difficultyLevel} label={guide.difficulty} />
              <MetaPill icon={<Waves className="w-3.5 h-3.5" />} label={guide.waveStyle} />
            </div>
          </div>
        </section>

        {/* Live conditions */}
        <section className="grid md:grid-cols-2 gap-4">
          {surfQ.isLoading && (
            <div className="clay p-6 flex items-center gap-2 text-[color:var(--ink-2)]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading live conditions…
            </div>
          )}
          {data && (
            <>
              <SurfReport data={data} />
              <TideChart tide={data.tide} moon={data.moon} />
            </>
          )}
        </section>

        {/* Quick facts */}
        <section className="clay p-5 sm:p-6" data-testid="beach-quickfacts">
          <p className="eyebrow text-[color:var(--coral)]">Quick facts</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <QuickFact icon={<Sun className="w-4 h-4" />} title="Best tide" value={guide.bestTide} />
            <QuickFact icon={<Wind className="w-4 h-4" />} title="Best wind" value={guide.bestWind} />
            <QuickFact icon={<Compass className="w-4 h-4" />} title="Wave style" value={guide.waveStyle} />
            <QuickFact icon={<Umbrella className="w-4 h-4" />} title="Board" value={guide.boardRecommended} />
          </div>
        </section>

        {/* Seasons */}
        <section className="clay p-5 sm:p-6" data-testid="beach-seasons">
          <p className="eyebrow text-[color:var(--coral)]">When to surf here</p>
          <div className="mt-3 space-y-2">
            {guide.bestSeasons.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-32 text-sm font-semibold text-[color:var(--navy)] tabular-nums shrink-0">{s.months}</div>
                <div className="flex-1">
                  <div className="text-sm">{s.label}</div>
                  <div className="mt-1 h-2 bg-[color:var(--sand-2)] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(s.quality / 5) * 100}%`,
                        background: guide.color,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How to get there */}
        <section className="clay p-5 sm:p-6" data-testid="beach-directions">
          <p className="eyebrow text-[color:var(--coral)]">How to get there</p>
          <div className="mt-2 space-y-1.5 text-sm">
            <p className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[color:var(--ink-2)]" />
              <span><strong>{guide.directions.distance}</strong> from {guide.directions.from}</span>
            </p>
            <p className="text-[color:var(--ink-2)] pl-6">{guide.directions.routeNote}</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={guide.directions.driveHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="beach-drive-link"
              className="pill px-4 py-2 bg-[color:var(--navy)] text-white text-sm inline-flex items-center gap-1.5 hover:bg-[color:var(--navy)]/90"
            >
              <Car className="w-4 h-4" /> Drive from Popoyo
            </a>
            <a
              href={guide.directions.mapHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="beach-map-link"
              className="pill px-4 py-2 border border-[color:var(--navy)] text-[color:var(--navy)] text-sm inline-flex items-center gap-1.5 hover:bg-[color:var(--sand-2)]"
            >
              <ExternalLink className="w-4 h-4" /> Open in Google Maps
            </a>
          </div>
        </section>

        {/* Sponsored — Nicaragua Taxi CTA. Placed right below "How to get there"
            so the transportation-intent moment converts. Uses a rel=sponsored
            + noopener external link + Nicaragua-Taxi's official brand logo. */}
        <section
          className="clay overflow-hidden relative"
          data-testid="beach-taxi-cta"
          style={{
            background: "radial-gradient(circle at 50% 0%, #0F2340 0%, #0A1628 65%, #060D1A 100%)",
            color: "#fff",
          }}
        >
          <div className="px-6 sm:px-10 py-6 sm:py-8 grid md:grid-cols-[auto,1fr,auto] items-center gap-5">
            <img
              src="/brand/nicaragua-taxi-logo.png"
              alt="Nicaragua Taxi Services"
              loading="lazy"
              className="w-40 sm:w-48 md:w-52 mx-auto md:mx-0 drop-shadow-[0_10px_22px_rgba(255,200,40,0.18)]"
            />
            <div className="text-center md:text-left">
              <p className="eyebrow" style={{ color: "rgba(255,199,0,0.9)" }}>Sponsored</p>
              <h3
                className="text-xl sm:text-2xl font-bold leading-tight mt-1"
                style={{ fontFamily: "Outfit", textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}
              >
                Need a taxi or transport to {guide.name}?
              </h3>
              <p className="text-sm text-white/75 mt-1.5">
                Airport pickups, city transfers, day trips, and shuttles across Nicaragua — trusted drivers, fixed fares.
              </p>
            </div>
            <a
              href="https://www.NicaraguaTaxi.com"
              target="_blank"
              rel="sponsored noopener noreferrer"
              data-testid="beach-taxi-cta-link"
              className="inline-flex items-center gap-2 pill px-5 py-3 text-sm font-bold text-[#0A1628] hover:scale-[1.03] transition-transform shrink-0"
              style={{ background: "#FFC700", boxShadow: "0 8px 22px rgba(255,199,0,0.35)" }}
            >
              <ExternalLink className="w-4 h-4" />
              Book with NicaraguaTaxi.com
            </a>
          </div>
        </section>

        {/* Local tips + hazards */}
        <section className="grid md:grid-cols-2 gap-4">
          <div className="clay p-5 sm:p-6" data-testid="beach-tips">
            <p className="eyebrow flex items-center gap-1.5 text-[color:var(--teal)]">
              <Sparkles className="w-3.5 h-3.5" /> Local tips
            </p>
            <ul className="mt-2 space-y-1.5 text-sm">
              {guide.localTips.map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[color:var(--teal)] mt-1">•</span><span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="clay p-5 sm:p-6" data-testid="beach-hazards">
            <p className="eyebrow flex items-center gap-1.5 text-[color:var(--emergency)]">
              <TriangleAlert className="w-3.5 h-3.5" /> Hazards
            </p>
            <ul className="mt-2 space-y-1.5 text-sm">
              {guide.hazards.map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[color:var(--emergency)] mt-1">•</span><span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Other beaches */}
        <section className="clay p-5 sm:p-6" data-testid="beach-neighbors">
          <p className="eyebrow text-[color:var(--coral)]">Explore other Popoyo beaches</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.values(BEACH_GUIDES)
              .filter((b) => b.slug !== guide.slug)
              .map((b) => localizeBeach(b, lang))
              .map((b) => (
                <Link
                  key={b.slug}
                  to={`/surf/${b.slug}`}
                  data-testid={`neighbor-${b.slug}`}
                  className="clay p-3 hover:scale-[1.02] transition text-center"
                  style={{ background: `${b.color}18` }}
                >
                  <p className="font-semibold text-sm" style={{ color: b.color }}>{b.name.replace("Playa ", "")}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mt-0.5">{b.difficulty}</p>
                </Link>
              ))}
          </div>
        </section>
      </main>

      <BackToTopButton />
      <Footer />
    </div>
  );
}

function DifficultyPill({ level = 3, label }) {
  const dots = [1, 2, 3, 4, 5];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-black/25 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
      {dots.map((d) => (
        <span key={d} className="w-1.5 h-1.5 rounded-full" style={{ background: d <= level ? "white" : "rgba(255,255,255,0.35)" }} />
      ))}
      {label}
    </span>
  );
}

function MetaPill({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-black/25 px-3 py-1 text-xs font-medium text-white backdrop-blur max-w-full">
      {icon}<span className="truncate">{label}</span>
    </span>
  );
}

function QuickFact({ icon, title, value }) {
  return (
    <div className="rounded-2xl bg-white/70 p-3">
      <p className="text-[11px] uppercase tracking-widest text-[color:var(--ink-2)] flex items-center gap-1.5">{icon}{title}</p>
      <p className="text-sm font-semibold text-[color:var(--navy)] mt-0.5">{value}</p>
    </div>
  );
}
